import { SideNavigationProps } from '@amzn/awsui-components-core';
import { useEffect, useState } from 'react';
import { ENVIRONMENT_VARIABLES } from '../../constants/EnvironmentConstants';
import {
  getFinSuiteNavigationItems,
  getNavigationItemsBySection,
  getOperationalExcellenceNavigationItems,
  ROUTE_CONFIG
} from '../../utilities/RouteConfig';
import { useAppContext } from '../context/AppContextProvider';
import { useAuth } from '../context/AuthContextProvider';

export interface NavItem {
  text: string;
  href: string;
  external?: boolean;
  type: 'link' | 'divider' | 'expandable-group' | 'section-header';
  children?: NavItem[];
  defaultExpanded?: boolean;
  bold?: boolean;
  indented?: boolean;
}

/**
 * Transforms SideNavigationProps.Item[] into our flat NavItem[] format.
 */
const transformItems = (items: SideNavigationProps.Item[]): NavItem[] => {
  return items.reduce<NavItem[]>((acc, item) => {
    if (item.type === 'divider') {
      acc.push({ text: '', href: '', type: 'divider' });
    } else if (item.type === 'link') {
      acc.push({ text: item.text, href: item.href || '', external: item.external, type: 'link' });
    } else if (item.type === 'expandable-link-group') {
      acc.push({
        text: item.text,
        href: item.href,
        type: 'expandable-group',
        defaultExpanded: item.defaultExpanded,
        children: transformItems([...item.items])
      });
    }
    return acc;
  }, []);
};

/**
 * Hook that builds the same navigation items as DFPHomePageSideNavigation
 * but returns them in a simplified NavItem[] format.
 */
export const useNavigationItems = (): NavItem[] => {
  const userAuthenticationDetails = useAuth();
  const { homePageContent } = useAppContext();
  const [navItems, setNavItems] = useState<NavItem[]>([]);

  useEffect(() => {
    const baseItems = getFinSuiteNavigationItems(userAuthenticationDetails.userLDAPGroups, ENVIRONMENT_VARIABLES.env.Stage);
    const transformed: NavItem[] = transformItems(baseItems);

    // Add Org Tools section
    const orgToolsItems = getNavigationItemsBySection('org-tools', userAuthenticationDetails.userLDAPGroups, ENVIRONMENT_VARIABLES.env.Stage);
    if (orgToolsItems.length > 0) {
      transformed.push({ text: '', href: '', type: 'divider' });
      transformed.push({ text: 'Org Tools', href: '', type: 'section-header', bold: true });
      orgToolsItems.forEach((item) => {
        if (item.type === 'link') {
          transformed.push({
            text: item.text,
            href: item.href || '',
            type: 'link'
          });
        }
      });
    }

    // Kingpin section
    const kingpinItems = getNavigationItemsBySection('kingpin', userAuthenticationDetails.userLDAPGroups, ENVIRONMENT_VARIABLES.env.Stage);
    if (kingpinItems.length > 0) {
      transformed.push({ text: '', href: '', type: 'divider' });
      transformed.push({ text: 'Kingpin', href: '', type: 'section-header', bold: true });
      kingpinItems.forEach((item) => {
        if (item.type === 'link') {
          transformed.push({
            text: item.text,
            href: item.href || '',
            type: 'link'
          });
        }
      });
    }

    // OE section - now visible to all users (previously gated by isOEReadOnly)
    {
      // Add divider before OE section
      transformed.push({ text: '', href: '', type: 'divider' });

      // Add OE parent as a section header with link
      const oeConfig = ROUTE_CONFIG['/operational-excellence'];
      transformed.push({
        text: oeConfig.navigationText || oeConfig.breadcrumbText,
        href: oeConfig.path,
        type: 'section-header',
        bold: true
      });

      // Add OE child items as flat links
      const oeChildItems = getOperationalExcellenceNavigationItems();
      oeChildItems.forEach((item) => {
        if (item.type === 'link') {
          transformed.push({
            text: item.text,
            href: item.href || '',
            type: 'link'
          });
        }
      });
    }

    setNavItems(transformed);
  }, [userAuthenticationDetails]);

  return navItems;
};
